import DOMPurify from "dompurify";

export const DESCRIPTION_TAGS = [
  "div", "p", "span", "br", "strong", "b", "em", "i", "u", "s",
  "ul", "ol", "li", "a",
];

const MIN_LINE_HEIGHT = { px: 12, "": 0.8, em: 0.8, rem: 0.8 };
const MAX_LINE_HEIGHT = { px: 72, "": 4, em: 4, rem: 4 };
const MAX_VERTICAL_MARGIN = { px: 64, em: 4, rem: 4 };

// Some captured social posts represent native emoji as an image with only an
// alt attribute (for example, <img alt="😀">). Images are intentionally not
// allowed in descriptions, so preserve just the safe, accessible emoji text
// before the HTML is sanitized or converted to plain text.
const EMOJI_PATTERN = /(?:\p{Extended_Pictographic}|\p{Regional_Indicator}|[#*0-9]\uFE0F?\u20E3)/u;

export function replaceEmojiImagesWithAlt(root) {
  if (!root?.querySelectorAll) return root;

  root.querySelectorAll("img[alt]").forEach((image) => {
    const alt = image.getAttribute("alt") || "";
    if (EMOJI_PATTERN.test(alt)) image.replaceWith(document.createTextNode(alt));
  });

  return root;
}

export function validateDescriptionLineHeight(value) {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const match = String(value).trim().toLowerCase().match(/^(\d*\.?\d+)\s*(px|em|rem)?$/);
  if (!match) return undefined;

  const number = Number(match[1]);
  const unit = match[2] || "";
  if (!Number.isFinite(number) || number <= 0) return undefined;

  const clamped = Math.min(Math.max(number, MIN_LINE_HEIGHT[unit]), MAX_LINE_HEIGHT[unit]);
  return `${Number(clamped.toFixed(3))}${unit}`;
}

export function validateDescriptionMargin(value) {
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const match = String(value).trim().toLowerCase().match(/^(\d*\.?\d+)\s*(px|em|rem)?$/);
  if (!match) return undefined;

  const number = Number(match[1]);
  const unit = match[2] || "";
  if (!Number.isFinite(number) || number < 0 || (number !== 0 && !unit)) return undefined;
  if (number === 0) return "0";

  const clamped = Math.min(number, MAX_VERTICAL_MARGIN[unit]);
  return `${Number(clamped.toFixed(3))}${unit}`;
}

function sanitizeStyle(styleText) {
  if (!styleText) return "";
  const safeDeclarations = [];

  styleText.split(";").forEach((declaration) => {
    const separator = declaration.indexOf(":");
    if (separator === -1) return;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration.slice(separator + 1).trim().toLowerCase();
    if (!value || /url\s*\(|expression\s*\(|var\s*\(|!important/i.test(value)) return;

    if (property === "line-height") {
      const lineHeight = validateDescriptionLineHeight(value);
      if (lineHeight) safeDeclarations.push(`line-height: ${lineHeight}`);
    } else if (property === "margin-top" || property === "margin-bottom") {
      const margin = validateDescriptionMargin(value);
      if (margin) safeDeclarations.push(`${property}: ${margin}`);
    } else if (property === "font-weight" && /^(normal|bold|bolder|lighter|[1-9]00)$/.test(value)) {
      safeDeclarations.push(`font-weight: ${value}`);
    } else if (property === "font-style" && /^(normal|italic)$/.test(value)) {
      safeDeclarations.push(`font-style: ${value}`);
    } else if (property === "text-decoration" && /^(none|underline|line-through|underline line-through|line-through underline)$/.test(value)) {
      safeDeclarations.push(`text-decoration: ${value}`);
    } else if (property === "white-space" && /^(normal|pre|pre-wrap|pre-line|break-spaces)$/.test(value)) {
      safeDeclarations.push(`white-space: ${value}`);
    }
  });

  return safeDeclarations.join("; ");
}

function isSafeLink(href) {
  if (!href) return false;
  const normalized = href.trim();
  if (/^(#|\/)/.test(normalized) && !normalized.startsWith("//")) return true;
  try {
    const parsed = new URL(normalized, window.location.origin);
    return ["http:", "https:", "mailto:", "tel:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}

export function sanitizeDescriptionHtml(html) {
  if (typeof html !== "string" || !/\S/.test(html)) return "";
  const source = document.createElement("template");
  source.innerHTML = html;
  replaceEmojiImagesWithAlt(source.content);

  const sanitized = DOMPurify.sanitize(source.innerHTML, {
    ALLOWED_TAGS: DESCRIPTION_TAGS,
    ALLOWED_ATTR: ["style", "href", "title"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
  const template = document.createElement("template");
  template.innerHTML = sanitized;

  template.content.querySelectorAll("*").forEach((element) => {
    const safeStyle = sanitizeStyle(element.getAttribute("style"));
    if (safeStyle) element.setAttribute("style", safeStyle);
    else element.removeAttribute("style");

    if (element.tagName === "A") {
      const href = element.getAttribute("href");
      if (!isSafeLink(href)) {
        element.removeAttribute("href");
        element.removeAttribute("title");
      } else {
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noopener noreferrer nofollow");
      }
    }
  });
  return template.innerHTML;
}

export function truncateHtmlByWords(html, maxWords) {
  if (!html || !Number.isInteger(maxWords) || maxWords <= 0) return { html, isTruncated: false };
  const container = document.createElement("div");
  container.innerHTML = html;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) textNodes.push(node);

  let wordsSeen = 0;
  let cutoffNode = null;
  let cutoffIndex = 0;
  let lastKeptNode = null;
  for (const textNode of textNodes) {
    const matches = [...textNode.data.matchAll(/\S+/g)];
    if (wordsSeen >= maxWords && matches.length) {
      cutoffNode = lastKeptNode;
      cutoffIndex = lastKeptNode?.data.length || 0;
      break;
    }
    if (wordsSeen + matches.length > maxWords) {
      const match = matches[maxWords - wordsSeen - 1];
      cutoffNode = textNode;
      cutoffIndex = match.index + match[0].length;
      break;
    }
    wordsSeen += matches.length;
    if (matches.length) lastKeptNode = textNode;
  }
  if (!cutoffNode) return { html, isTruncated: false };

  // Remove the complete DOM range after the word cutoff. Removing only later
  // text nodes leaves empty paragraphs and <br> elements that still consume
  // the saved line height in the collapsed card.
  const range = document.createRange();
  range.setStart(cutoffNode, cutoffIndex);
  range.setEnd(container, container.childNodes.length);
  range.deleteContents();
  cutoffNode.data = `${cutoffNode.data.trimEnd()}…`;

  container.querySelectorAll("p:empty, div:empty, span:empty, li:empty, ul:empty, ol:empty").forEach((element) => {
    element.remove();
  });
  return { html: container.innerHTML, isTruncated: true };
}
